import { useEffect, useMemo, useState } from "react";
import { getSubmissionsByMintSpace } from "../../api/submissions";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/TranslationContext";
import AppHeader from "../ds/AppHeader";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import PopUpButton from "../ds/PopUpButton";
import SegmentedControl from "../ds/SegmentedControl";
import StatCell from "../ds/StatCell";
import { APP_FILLED, APP_TINTED, PLATFORMS, PlatformLogo, SELECTED_TAB } from "./shared";

const DAY = 24 * 60 * 60 * 1000;
const BARS = 30;
const RANGES = [7, 30, 90, 0];

const compact = (n) => (n >= 1000 ? `${Math.round(n / 100) / 10}K` : String(Math.round(n * 100) / 100));
const money = (n) => `${(Number(n) || 0).toFixed(2)} ₾`;
const dayLabel = (time, language) =>
  new Date(time).toLocaleDateString(language === "ka" ? "ka-GE" : "en-GB", { day: "numeric", month: "short" });

/** Approved videos counted on the day they were approved. */
const approvedIn = (rows, from, to) =>
  rows.filter((row) => {
    if (row.status !== "approved") return false;
    const time = new Date(row.approvedAt || row.createdAt).getTime();
    return time >= from && time < to;
  });

const sum = (rows, field) => rows.reduce((total, row) => total + (Number(row[field]) || 0), 0);

/** Figma's bars: the deep colour, the tallest one in the base colour, the scale on the left. */
const BarPlot = ({ points, language }) => {
  const max = Math.max(...points.map((point) => point.value), 1);
  const peak = points.reduce((best, point, index) => (point.value > points[best].value ? index : best), 0);
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((share) => compact(max * share));
  const labels = [0, 0.25, 0.5, 0.75, 1].map((share) => points[Math.round((points.length - 1) * share)]);
  return (
    <>
      <div className="flex h-[200px] gap-space-12">
        <div className="flex w-[32px] shrink-0 flex-col justify-between text-caption-1 text-label-tertiary">
          {ticks.map((tick, index) => (
            <span key={index}>{tick}</span>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 items-end gap-[2px] border-b border-separator-default min-[600px]:gap-[6px]">
          {points.map((point, index) => (
            <span
              key={point.time}
              title={`${dayLabel(point.time, language)} · ${point.value}`}
              className={`min-w-0 flex-1 rounded-t-[4px] ${
                index === peak && point.value > 0 ? "bg-app-creator-rewards-base" : "bg-app-creator-rewards-deep"
              }`}
              style={{ height: `${Math.max((point.value / max) * 100, 2)}%` }}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-between pl-[44px] text-caption-1 text-label-tertiary">
        {labels.map((label) => (
          <span key={label.time}>{dayLabel(label.time, language)}</span>
        ))}
      </div>
    </>
  );
};

/**
 * Figma V6.1 and V6.2: a creator's own results in this space. Worked out from their
 * submissions, so the platform and date filters answer at once. A video counts on the day
 * it was approved, with the views it had then.
 */
const CreatorAnalytics = ({ mintSpaceId, activeCampaigns = 0, setToast, onFindCampaign }) => {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const userId = String(user?._id || user?.id || "");
  const [rows, setRows] = useState([]);
  const [platform, setPlatform] = useState("");
  const [days, setDays] = useState("30");
  const [chart, setChart] = useState("views");

  useEffect(() => {
    if (!mintSpaceId) return;
    getSubmissionsByMintSpace(mintSpaceId)
      .then((res) =>
        setRows((res?.data || []).filter((row) => String(row.submittedBy?._id || row.submittedBy) === userId))
      )
      .catch(() => setToast?.(t("common.error")));
  }, [mintSpaceId, userId, setToast, t]);

  const view = useMemo(() => {
    const span = Number(days);
    const now = Date.now();
    const mine = rows.filter((row) => !platform || row.platform === platform);
    const oldest = mine.reduce((first, row) => Math.min(first, new Date(row.createdAt).getTime()), now);
    const from = span ? now - span * DAY : oldest;
    const current = approvedIn(mine, from, now + 1);
    const prior = span ? approvedIn(mine, from - span * DAY, from) : [];
    const inRange = mine.filter((row) => new Date(row.createdAt).getTime() >= from);

    const buckets = Math.min(span || BARS, BARS);
    const width = Math.max(now - from, DAY) / buckets;
    const field = chart === "views" ? "viewCount" : "totalEarned";
    const points = Array.from({ length: buckets }, (_, index) => {
      const start = from + index * width;
      return { time: start, value: Math.round(sum(approvedIn(mine, start, start + width + (index === buckets - 1 ? 1 : 0)), field) * 100) / 100 };
    });

    const views = sum(current, "viewCount");
    const priorViews = sum(prior, "viewCount");
    return {
      views,
      change: priorViews > 0 ? Math.round(((views - priorViews) / priorViews) * 100) : null,
      earned: sum(current, "totalEarned"),
      pending: sum(current, "pendingPayout"),
      sent: inRange.length,
      approved: inRange.filter((row) => row.status === "approved").length,
      waiting: inRange.filter((row) => row.status === "pending").length,
      rejected: inRange.filter((row) => row.status === "rejected").length,
      campaigns: new Set(inRange.map((row) => String(row.campaign?._id || row.campaign))).size,
      points,
      hasValues: points.some((point) => point.value > 0),
    };
  }, [rows, platform, days, chart]);

  const rangeLabel = (value) => (value ? t("campaignFlow.lastDays", { count: value }) : t("campaignFlow.allTime"));

  return (
    <div className="flex flex-col gap-space-24 font-ds">
      <AppHeader
        app="creator-rewards"
        eyebrow="Creator Rewards"
        title={t("mintSpace.creatorPerformanceTitle")}
        subtitle={t("mintSpace.creatorPerformanceSubtitle")}
      />

      <div className="flex flex-col gap-space-12 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between">
        <div className="flex flex-wrap items-center gap-space-8">
          {PLATFORMS.map((item) => {
            const selected = platform === item.id;
            return (
              <Button
                key={item.id}
                variant="gray"
                size="s"
                aria-pressed={selected}
                icon={<PlatformLogo platform={item} size={16} />}
                className={selected ? APP_TINTED : ""}
                onClick={() => setPlatform(selected ? "" : item.id)}
              >
                {item.name}
              </Button>
            );
          })}
        </div>
        <PopUpButton
          label={t("campaignFlow.analyticsRange")}
          value={days}
          onChange={setDays}
          options={RANGES.map((value) => ({ value: String(value), label: rangeLabel(value) }))}
          className="self-start min-[600px]:self-auto"
        />
      </div>

      <div className="grid grid-cols-1 gap-space-12 min-[600px]:grid-cols-2 min-[600px]:gap-space-20 lg:gap-space-24 xl:grid-cols-4">
        <StatCell
          label={t("mintSpace.views")}
          value={compact(view.views)}
          meta={
            view.change === null
              ? t("campaignFlow.approvedVideoViews")
              : t("campaignFlow.changeFromPrior", {
                  change: `${view.change > 0 ? "+" : ""}${view.change}`,
                  count: days,
                })
          }
        />
        <StatCell
          label={t("mintSpace.earnings")}
          value={money(view.earned)}
          meta={t("campaignFlow.paidAndPending", { paid: money(view.earned - view.pending), pending: money(view.pending) })}
        />
        <StatCell
          label={t("campaignFlow.content")}
          value={t("campaignFlow.sentCount", { count: view.sent })}
          meta={t("campaignFlow.contentBreakdown", { approved: view.approved, pending: view.waiting, rejected: view.rejected })}
        />
        <StatCell
          label={t("mintSpace.activeCampaigns")}
          value={activeCampaigns}
          meta={t("campaignFlow.joinedCampaigns", { count: view.campaigns })}
        />
      </div>

      <section className="flex flex-col gap-space-20 rounded-[20px] bg-bg-raised px-space-16 py-space-24 min-[600px]:p-space-24">
        <div className="flex flex-col gap-space-16 min-[600px]:flex-row min-[600px]:items-center">
          <div className="flex min-w-0 flex-1 flex-col gap-space-4">
            <h2 className="text-headline text-label-primary">
              {t(chart === "views" ? "campaignFlow.viewsOverTime" : "campaignFlow.earningsOverTime")}
            </h2>
            <p className="text-footnote text-label-secondary">
              {t(chart === "views" ? "campaignFlow.viewsOverTimeHint" : "campaignFlow.earningsOverTimeHint")}
            </p>
          </div>
          <SegmentedControl
            track="material"
            label={t("mintSpace.views")}
            value={chart}
            onChange={setChart}
            selectedClass={SELECTED_TAB}
            className="shrink-0 [&>button]:px-space-8 min-[600px]:w-[220px]"
            options={[
              { value: "views", label: t("mintSpace.views") },
              { value: "earnings", label: t("mintSpace.earnings") },
            ]}
          />
        </div>
        {view.hasValues ? (
          <BarPlot points={view.points} language={language} />
        ) : (
          <div className="flex flex-col items-center gap-space-8 py-space-48 text-center">
            <span className="mb-space-8 flex size-14 items-center justify-center rounded-[18px] bg-app-creator-rewards-deep text-app-creator-rewards-soft">
              <Icon name="chart" size={28} />
            </span>
            <p className="text-headline text-label-primary">{t("campaignFlow.noViewsTitle")}</p>
            <p className="max-w-[420px] text-subheadline text-label-secondary">{t("campaignFlow.noViewsBody")}</p>
            <Button size="m" className={`mt-space-8 ${APP_FILLED}`} onClick={onFindCampaign}>
              {t("campaignFlow.findACampaign")}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default CreatorAnalytics;
